import React, { useState, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import {
  FiHome, FiDatabase, FiMenu, FiX, FiMoon, FiSun, FiLogOut, FiChevronDown, FiChevronRight, FiSearch, FiClock, FiBook, FiMapPin
} from 'react-icons/fi';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { NLPLogsProvider } from './context/NLPLogsContext';
import { NLPCacheProvider } from './context/NLPCacheContext';
import { DeviceProvider, useDevice } from './context/DeviceContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AgentChatProvider, useAgentChat } from './context/AgentChatContext';
import FloatingResearchBubble from './components/FloatingResearchBubble';
import AgentChatPanel from './components/AgentChatPanel';
import MobileConstellationLogo from './components/MobileConstellationLogo';
import LandingPage from './pages/LandingPage';
import CompanyDetail from './pages/CompanyDetail';
import InvestorDetail from './pages/InvestorDetail';
import DealDetail from './pages/DealDetail';
import DataExplorer from './pages/DataExplorer';
// Tools routes kept for direct URL access but removed from sidebar nav
import ToolsHub from './pages/ToolsHub';
import AdTechNavigator from './pages/AdTechNavigator';
import GuidePage from './pages/GuidePage';
import Timeline from './pages/Timeline';
import KnowledgeBase from './pages/KnowledgeBase';
import Whereabouts from './pages/Whereabouts';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import transcendLogoLight from './assets/transcend-logo-light.png';
import './App.css';
import './styles/tablet.css';

// Protected Route Component - Requires authentication
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f7fa'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#1a2744',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#6c757d' }}>Loading...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Error Boundary Component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🔴 ERROR CAUGHT:', error);
    console.error('📍 Component Stack:', errorInfo.componentStack);
    console.error('📊 Error Stack:', error.stack);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      return (
        <div style={{ padding: '40px', fontFamily: 'monospace', maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ color: '#dc2626' }}>🔴 Error Details</h2>

          <div style={{ background: '#fef2f2', border: '2px solid #dc2626', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
            <h3 style={{ margin: '0 0 8px 0' }}>Error Message:</h3>
            <p style={{ color: '#991b1b', fontSize: '16px', margin: 0 }}>{error?.message || 'Unknown error'}</p>
          </div>

          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
            <h3 style={{ margin: '0 0 8px 0' }}>Error Type:</h3>
            <p style={{ margin: 0 }}>{error?.name || 'Error'}</p>
          </div>

          <details style={{ background: '#f9fafb', border: '1px solid #e5e7eb', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>📊 Full Stack Trace</summary>
            <pre style={{ overflow: 'auto', fontSize: '12px', background: '#fff', padding: '12px', borderRadius: '4px' }}>
              {error?.stack || 'No stack trace available'}
            </pre>
          </details>

          <details style={{ background: '#f9fafb', border: '1px solid #e5e7eb', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>📍 Component Stack</summary>
            <pre style={{ overflow: 'auto', fontSize: '12px', background: '#fff', padding: '12px', borderRadius: '4px' }}>
              {errorInfo?.componentStack || 'No component stack available'}
            </pre>
          </details>

          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              background: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Wrapper for LandingPage that provides agent chat callbacks
function LandingPageWithAgent() {
  const { openChat, updateSearchContext } = useAgentChat();
  return (
    <LandingPage
      onOpenChat={openChat}
      onUpdateSearchContext={updateSearchContext}
    />
  );
}

// Global Agent Chat - Floating Bubble + Chat Panel (rendered once at app level)
function GlobalAgentChat() {
  const { activeAgent, chatPanelOpen, searchContext, pageContext, closeChat, switchAgent } = useAgentChat();

  return (
    <>
      <FloatingResearchBubble />
      <AgentChatPanel
        activeAgent={activeAgent}
        searchContext={searchContext}
        pageContext={pageContext}
        isOpen={chatPanelOpen}
        onClose={closeChat}
        onSwitchAgent={switchAgent}
        onNewResults={() => {}}
      />
    </>
  );
}

function Navigation({ isCollapsed, toggleSidebar, mobileMenuOpen, setMobileMenuOpen }) {
  const { isDarkMode, toggleTheme } = useTheme();
  const { user, logout, isAuthenticated } = useAuth();
  const { isMobile } = useDevice();
  const location = useLocation();
  const [intelOpen, setIntelOpen] = useState(
    location.pathname === '/intelligence' || location.pathname === '/data-explorer'
    || location.pathname.startsWith('/company/') || location.pathname.startsWith('/investor/')
    || location.pathname.startsWith('/deal/') || location.pathname.startsWith('/timeline')
    || location.pathname === '/knowledge-base'
  );

  const handleLinkClick = () => {
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const showExpandedContent = !isMobile && !isCollapsed;

  const sidebarClasses = [
    'sidebar-nav',
    !isMobile && isCollapsed ? 'collapsed' : '',
    mobileMenuOpen ? 'mobile-open' : ''
  ].filter(Boolean).join(' ');

  const isIntelActive = location.pathname === '/intelligence' || location.pathname === '/data-explorer'
    || location.pathname.startsWith('/company/') || location.pathname.startsWith('/investor/')
    || location.pathname.startsWith('/deal/') || location.pathname.startsWith('/timeline')
    || location.pathname === '/knowledge-base';

  return (
    <nav className={sidebarClasses}>
      <div className="nav-brand">
        <Link to="/" className="brand-link" onClick={handleLinkClick}>
          {showExpandedContent && (
            <>
              <img src={transcendLogoLight} alt="Transcend" className="brand-logo" />
              <span className="brand-subtitle">Intelligence Platform</span>
            </>
          )}
          {!showExpandedContent && !isMobile && <span className="brand-logo-collapsed">T</span>}
          {!showExpandedContent && isMobile && (
            <MobileConstellationLogo size="medium" />
          )}
        </Link>
      </div>

      <div className="nav-links">
        {/* Guide = Home */}
        <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`} onClick={handleLinkClick}>
          <span className="nav-icon"><FiHome size={20} /></span>
          {showExpandedContent && <span>Guide</span>}
        </Link>

        {/* Intelligence with submenu */}
        <button
          className={`nav-link nav-link-btn ${isIntelActive ? 'active' : ''}`}
          onClick={() => setIntelOpen(!intelOpen)}
        >
          <span className="nav-icon"><FiSearch size={20} /></span>
          {showExpandedContent && (
            <>
              <span>Intelligence</span>
              <span className="nav-submenu-arrow">
                {intelOpen ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
              </span>
            </>
          )}
        </button>
        {intelOpen && (
          <div className="nav-submenu">
            <Link
              to="/intelligence"
              className={`nav-link nav-sublink ${location.pathname === '/intelligence' ? 'active' : ''}`}
              onClick={handleLinkClick}
            >
              <span className="nav-icon"><FiSearch size={16} /></span>
              {showExpandedContent && <span>Search</span>}
            </Link>
            {!isMobile && (
              <Link
                to="/data-explorer"
                className={`nav-link nav-sublink ${location.pathname === '/data-explorer' ? 'active' : ''}`}
                onClick={handleLinkClick}
              >
                <span className="nav-icon"><FiDatabase size={16} /></span>
                {showExpandedContent && <span>Data Explorer</span>}
              </Link>
            )}
            <Link
              to="/timeline"
              className={`nav-link nav-sublink ${location.pathname.startsWith('/timeline') ? 'active' : ''}`}
              onClick={handleLinkClick}
            >
              <span className="nav-icon"><FiClock size={16} /></span>
              {showExpandedContent && <span>Timeline</span>}
            </Link>
            <Link
              to="/knowledge-base"
              className={`nav-link nav-sublink ${location.pathname === '/knowledge-base' ? 'active' : ''}`}
              onClick={handleLinkClick}
            >
              <span className="nav-icon"><FiBook size={16} /></span>
              {showExpandedContent && <span>Knowledge Base</span>}
            </Link>
          </div>
        )}

        {/* Team Whereabouts */}
        <Link to="/whereabouts" className={`nav-link ${location.pathname === '/whereabouts' ? 'active' : ''}`} onClick={handleLinkClick}>
          <span className="nav-icon"><FiMapPin size={20} /></span>
          {showExpandedContent && <span>Whereabouts</span>}
        </Link>
      </div>

      {/* Bottom section — user info, theme toggle, collapse, sign out */}
      <div className="nav-bottom-section">
        {/* User Info */}
        {isAuthenticated && user && showExpandedContent && (
          <div className="nav-user-section">
            <div className="nav-user-info">
              <span className="nav-user-name">{user.name}</span>
              <span className="nav-user-email">{user.email}</span>
            </div>
          </div>
        )}

        {/* Dark Mode Toggle */}
        <button className="theme-toggle-btn" onClick={toggleTheme} title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
          <span className="nav-icon">
            {isDarkMode ? <FiSun size={18} /> : <FiMoon size={18} />}
          </span>
          {showExpandedContent && <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>}
        </button>

        {/* Desktop Sidebar Toggle */}
        {!isMobile && (
          <button className="sidebar-toggle" onClick={toggleSidebar} title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <FiMenu size={18} />
            {showExpandedContent && <span>Collapse</span>}
          </button>
        )}

        {/* Sign Out */}
        {isAuthenticated && (
          <button className="logout-btn-bottom" onClick={logout} title="Sign Out">
            <span className="nav-icon"><FiLogOut size={18} /></span>
            {showExpandedContent && <span>Sign Out</span>}
          </button>
        )}
      </div>
    </nav>
  );
}

// Inner App component that can use useLocation
function AppContent() {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isMobile, isDesktop } = useDevice();
  const location = useLocation();

  // Check if we're on auth-related pages (hide sidebar)
  const isAuthPage = ['/login', '/auth/callback', '/auth/success'].includes(location.pathname);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Build App class names based on device type
  const appClasses = [
    'App',
    isMobile ? 'mobile-mode' : '',
    isDesktop ? 'desktop-mode' : ''
  ].filter(Boolean).join(' ');

  // Determine main content margin class
  const getMainContentClass = () => {
    if (isMobile) return '';
    return isCollapsed ? 'sidebar-collapsed' : '';
  };

  // For auth pages, render without sidebar
  if (isAuthPage) {
    return (
      <div className="App auth-page-layout">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/success" element={<AuthCallback />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className={appClasses}>
      {/* Mobile Menu Button - only on mobile */}
      {isMobile && (
        <button className="mobile-menu-button" onClick={toggleMobileMenu}>
          {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
      )}

      {/* Mobile Overlay */}
      {isMobile && mobileMenuOpen && (
        <div className="mobile-overlay" onClick={toggleMobileMenu}></div>
      )}

      <Navigation
        isCollapsed={isCollapsed}
        toggleSidebar={toggleSidebar}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <main className={`main-content ${getMainContentClass()}`}>
        <Routes>
          {/* Guide is the home page */}
          <Route path="/" element={
            <ProtectedRoute><GuidePage /></ProtectedRoute>
          } />
          {/* Intelligence (formerly Home) */}
          <Route path="/intelligence" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <LandingPageWithAgent />
              </ErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/company/:pitchbookId" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <CompanyDetail />
              </ErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/investor/:pitchbookInvestorId" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <InvestorDetail />
              </ErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/deal/:pitchbookDealId" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <DealDetail />
              </ErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/data-explorer" element={
            <ProtectedRoute><DataExplorer /></ProtectedRoute>
          } />
          <Route path="/knowledge-base" element={
            <ProtectedRoute><ErrorBoundary><KnowledgeBase /></ErrorBoundary></ProtectedRoute>
          } />
          <Route path="/whereabouts" element={
            <ProtectedRoute><ErrorBoundary><Whereabouts /></ErrorBoundary></ProtectedRoute>
          } />
          <Route path="/timeline" element={
            <ProtectedRoute><ErrorBoundary><Timeline /></ErrorBoundary></ProtectedRoute>
          } />
          <Route path="/timeline/:companyName" element={
            <ProtectedRoute><ErrorBoundary><Timeline /></ErrorBoundary></ProtectedRoute>
          } />
          <Route path="/tools" element={
            <ProtectedRoute><ToolsHub /></ProtectedRoute>
          } />
          <Route path="/tools/ad-tech" element={
            <ProtectedRoute><AdTechNavigator /></ProtectedRoute>
          } />
          {/* Redirect old /guide URL to home */}
          <Route path="/guide" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* Global Research Agent - Floating Bubble + Chat Panel */}
      <GlobalAgentChat />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

// Wrap App with all providers
function AppWithProviders() {
  return (
    <AuthProvider>
      <DeviceProvider>
        <NLPCacheProvider>
          <NLPLogsProvider>
            <ThemeProvider>
              <AgentChatProvider>
                <App />
              </AgentChatProvider>
            </ThemeProvider>
          </NLPLogsProvider>
        </NLPCacheProvider>
      </DeviceProvider>
    </AuthProvider>
  );
}

export default AppWithProviders;
